import { Avatar, AvatarSize, AvatarStyle } from "@beydesign/storybook"
import { ReceivedChatMessage } from "@livekit/components-react"
import { Box } from "@mui/material"
import dayjs from "dayjs"
import React from "react"
import { useAppContext } from "../../app/Context"
import { decodeMessage } from "../../utils/func"

interface ChatBoxProps {
  item: ReceivedChatMessage
}

export function ChatBox({ item }: ChatBoxProps) {
  const context = useAppContext()
  const isMessageFromAgent = item?.from.isAgent
  const name = isMessageFromAgent
    ? context.agent?.name || "Agent"
    : context.user?.name || "User"
  const messageContent = decodeMessage(item?.message) || "No message"

  return (
    <Box
      data-testid="chat-message-item"
      data-sender-type={isMessageFromAgent ? "agent" : "user"}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
        width: "100%",
      }}
    >
      <Avatar
        text={name}
        size={AvatarSize.md}
        avatarStyle={AvatarStyle.Letters}
        style={{
          backgroundColor: isMessageFromAgent
            ? "var(--call-bubble-agent-bg)"
            : "var(--call-bubble-user-bg)",
          color: "var(--call-bubble-text)",
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-tokens-size-in-px-spacing-spacing-xs)",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            color: "var(--color-text-text-white)",
          }}
        >
          <Box
            component="span"
            data-testid="chat-message-sender"
            sx={{
              fontSize: "var(--global-text-s-semi-bold-font-size)",
              fontWeight: "var(--global-text-s-semi-bold-font-weight)",
              lineHeight:
                "calc(var(--global-text-s-semi-bold-line-height) * 1px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Box>
          <Box
            component="span"
            data-testid="chat-message-timestamp"
            sx={{
              fontSize: "var(--global-text-xs-regular-font-size)",
              lineHeight:
                "calc(var(--global-text-xs-regular-line-height) * 1px)",
              flexShrink: 0,
            }}
          >
            {dayjs(item.timestamp).format("h:mm A")}
          </Box>
        </Box>
        <Box
          data-testid={`chat-message-bubble-${isMessageFromAgent ? "agent" : "user"}`}
          data-message-content={messageContent}
          sx={{
            paddingX: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            paddingY: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
            borderRadius: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            borderTopLeftRadius: 0,
            backgroundColor: isMessageFromAgent
              ? "var(--call-bubble-agent-bg)"
              : "var(--call-bubble-user-bg)",
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            fontSize: "var(--global-text-m-regular-font-size)",
            lineHeight: "calc(var(--global-text-m-regular-line-height) * 1px)",
            color: "var(--call-bubble-text)",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          <span
            data-testid={
              isMessageFromAgent
                ? "chat-message-text"
                : "chat-message-text-user"
            }
          >
            {messageContent}
          </span>
        </Box>
      </Box>
    </Box>
  )
}
